import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { UploadFile } from '../../types/upload';

interface FileUploaderProps {
  onFilesAdded: (files: File[]) => void;
  uploadedFiles: UploadFile[];
  maxFiles?: number;
  onRemoveFile?: (fileId: string) => void;
}

export const FileUploader: React.FC<FileUploaderProps> = ({ 
  onFilesAdded, 
  uploadedFiles,
  maxFiles = 10,
  onRemoveFile 
}) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Filter out duplicates
    const newFiles = acceptedFiles.filter(file => 
      !uploadedFiles.some(uf => uf.file.name === file.name)
    );
    
    if (newFiles.length > 0) {
      onFilesAdded(newFiles);
    }
  }, [uploadedFiles, onFilesAdded]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    maxFiles,
    multiple: true
  });

  const remainingSlots = maxFiles - uploadedFiles.length;

  return (
    <div className="w-full">
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
          transition-all duration-200 ease-in-out
          ${isDragActive 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400 bg-gray-50'
          }
          ${remainingSlots === 0 ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} disabled={remainingSlots === 0} />
        
        <svg 
          className="mx-auto h-12 w-12 text-gray-400 mb-4"
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" 
          />
        </svg>

        {isDragActive ? (
          <p className="text-lg text-blue-600">Drop your files here...</p>
        ) : (
          <>
            <p className="text-lg text-gray-700 mb-2">
              Drag & drop your lab reports here, or click to select
            </p>
            <p className="text-sm text-gray-500 mb-1">
              Supports: PDF reports, CSV/Excel exports from Quest, LabCorp, and more
            </p>
            <p className="text-sm text-gray-500">
              Upload one or multiple files • {remainingSlots > 0 
                ? `Up to ${maxFiles} files at once`
                : 'Maximum files reached'
              }
            </p>
          </>
        )}
      </div>

      {/* File list */}
      {uploadedFiles.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            Uploaded Files ({uploadedFiles.length})
          </h3>
          <div className="space-y-2">
            {uploadedFiles.map((file) => (
              <FileItem 
                key={file.id} 
                uploadFile={file} 
                onRemove={onRemoveFile ? () => onRemoveFile(file.id) : undefined}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const FileItem: React.FC<{ uploadFile: UploadFile; onRemove?: () => void }> = ({ uploadFile, onRemove }) => {
  const getStatusIcon = () => {
    // Show duplicate icon if duplicate detected
    if (uploadFile.isDuplicate) {
      return '🔁';
    }
    
    switch (uploadFile.status) {
      case 'pending':
        return '⏳';
      case 'processing':
        return '⚙️';
      case 'extracted':
        return '✅';
      case 'error':
        return '❌';
    }
  };

  const getStatusColor = () => {
    // Show warning color for duplicates
    if (uploadFile.isDuplicate) {
      return 'text-orange-500';
    }
    
    switch (uploadFile.status) {
      case 'pending':
        return 'text-gray-500';
      case 'processing':
        return 'text-blue-500';
      case 'extracted':
        return 'text-green-500';
      case 'error':
        return 'text-red-500';
    }
  };

  return (
    <div className={`flex items-center justify-between p-3 bg-white rounded-lg border ${
      uploadFile.isDuplicate ? 'border-orange-300 bg-orange-50' : 'border-gray-200'
    }`}>
      <div className="flex items-center space-x-3">
        <span className="text-2xl">{getStatusIcon()}</span>
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">
            {uploadFile.file.name}
          </p>
          <p className={`text-xs ${getStatusColor()}`}>
            {uploadFile.isDuplicate && uploadFile.duplicateInfo ? uploadFile.duplicateInfo.message : (
              uploadFile.status === 'processing' ? 'Extracting data...' :
              uploadFile.status === 'extracted' ? 'Ready for review' :
              uploadFile.status === 'error' ? uploadFile.error :
              uploadFile.status === 'pending' ? 'Waiting...' : ''
            )}
          </p>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <span className="text-xs text-gray-500">
          {(uploadFile.file.size / 1024 / 1024).toFixed(2)} MB
        </span>
        {onRemove && (
          <button
            onClick={onRemove}
            className="ml-2 p-1 text-gray-400 hover:text-red-600 transition-colors"
            title={uploadFile.isDuplicate ? "Remove duplicate" : "Remove file"}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};